import { Box, CircularProgress, Divider } from '@mui/material';
import { PrimaryButton } from 'components/atoms/Buttons';
import { MediumText, NormalText } from 'components/atoms/Typography';
import CartSwitch from 'components/CartSwitch';
import { db } from 'config/firebase';
import { Cart, Payment, PaymentType } from 'klikni-jadi-shared-stuff';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/reducers';
import { useCartService } from 'services/cart/useCartService';
import walletIcon from 'assets/svg/wallet.svg';
import { settingsPath } from 'services/settings-path';
import Coupon from './components/Coupon';
import Render from 'components/atoms/Render';
import ErrorIcon from '@mui/icons-material/Error';
import { isNumber } from 'lodash';

interface ICartFeesProps {
    cart?: Cart;
    onProceed?: () => void;
    loading?: boolean;
    error?: string;
    // onlineFee?: number;
    paymentType?: Payment;
    disabled?: boolean;
}

const CartFees = ({ cart, onProceed, loading, error, paymentType, disabled }: ICartFeesProps) => {
    const { t } = useTranslation('common');

    const { getFinalCartPrice } = useCartService();
    const currentUser = useSelector((state: RootState) => state.currentUser).data;
    const [useWallet, setUseWallet] = useState<boolean>(false);
    const [roundUp, setRoundUp] = useState<boolean>(false);
    const { addWallet, addRoundup, getUsedWalletMoney, loadingPrice } = useCartService();

    useEffect(() => {
        if (cart?.wallet > 0) {
            setUseWallet(true);
        }
        if (cart?.roundup > 0) {
            setRoundUp(true);
        }
    }, [cart?.groupedItems]);

    const hasDiscountedItemsPrice = useMemo(() => {
        if (cart?.type === 'takeout' && cart?.takeoutSavings > cart?.takeoutDiscount) return true;

        return cart?.itemsTotalCost !== cart?.itemsTotalPrice;
    }, [cart?.takeoutSavings, cart?.takeoutDiscount, cart?.itemsTotalCost, cart?.itemsTotalPrice, cart?.type]);

    const priceBefore = useMemo(() => {
        if (cart?.type === 'takeout' && cart?.takeoutSavings > cart?.takeoutDiscount)
            return cart?.itemsTotalCost + ((cart?.takeoutSavings || 0) - (cart?.takeoutDiscount || 0));

        return cart?.itemsTotalPrice;
    }, [cart?.takeoutSavings, cart?.takeoutDiscount, cart?.itemsTotalCost]);

    // useEffect(() => {
    //     if (cart?.itemsTotalPrice < Object.values(cart?.groupedItems)?.[0]?.restaurantMinOrderAmount) {
    //         addRoundup(true, Object.values(cart?.groupedItems)?.[0]?.restaurantId);
    //     }
    // }, []);
    return (
        <Box
            display="flex"
            minWidth={{ xs: '100%' }}
            flexDirection="column"
            justifyContent="space-between"
            py={2}
            px={3}
            mt={{ xs: 3, md: -10 }}
            zIndex={9999999}
            sx={{ boxShadow: '0px 10px 20px rgba(0, 0, 0, 0.15)', borderRadius: '5px', backgroundColor: 'white' }}
        >
            <Box display="flex" flexDirection="column">
                <Box display="flex" width="100%" alignItems="center" justifyContent="space-between">
                    {' '}
                    <MediumText>{t('order_items')}</MediumText>
                    {hasDiscountedItemsPrice && (
                        <Box display="flex" gap={1}>
                            <MediumText
                                sx={{
                                    textDecoration: 'line-through',
                                    color: '#a0a0a0',
                                }}
                            >
                                {priceBefore} {t('mkd')}
                            </MediumText>
                            <MediumText>
                                {cart?.itemsTotalCost || 0} {t('mkd')}
                            </MediumText>
                        </Box>
                    )}
                    {!hasDiscountedItemsPrice && (
                        <Box>
                            <MediumText>
                                {cart?.itemsTotalCost} {t('mkd')}
                            </MediumText>
                        </Box>
                    )}
                </Box>
                <Render when={!!cart?.roundup}>
                    <Box display="flex" width="100%" alignItems="center" justifyContent="space-between">
                        {' '}
                        <MediumText>{t('roundup')}</MediumText>
                        <MediumText>
                            {cart?.roundup} {t('mkd')}
                        </MediumText>
                    </Box>
                </Render>
                <Render when={cart?.type === 'delivery' && !isNaN(cart?.deliveryFee) && cart?.deliveryFee >= 0}>
                    {' '}
                    <Box display="flex" width="100%" alignItems="center" justifyContent="space-between">
                        {' '}
                        <MediumText>{t('delivery')}</MediumText>
                        <MediumText>
                            {cart?.deliveryFee} {t('mkd')}
                        </MediumText>
                    </Box>
                </Render>
                <Render when={!!cart?.priorityFee}>
                    <Box display="flex" width="100%" alignItems="center" justifyContent="space-between">
                        {' '}
                        <MediumText>{t('priorityFee')}</MediumText>
                        <MediumText>
                            {cart?.priorityFee} {t('mkd')}
                        </MediumText>
                    </Box>
                </Render>
                <Render when={!!cart?.serviceFee}>
                    <Box display="flex" width="100%" alignItems="center" justifyContent="space-between">
                        {' '}
                        <MediumText>{t('serviceFee')}</MediumText>
                        <MediumText>
                            {cart?.serviceFee} {t('mkd')}
                        </MediumText>
                    </Box>
                </Render>
                {paymentType === 'online' && !!cart?.onlineFee && (
                    <Box display="flex" width="100%" alignItems="center" justifyContent="space-between">
                        {' '}
                        <MediumText>{t('online_fee')}</MediumText>
                        <MediumText>
                            {cart?.onlineFee} {t('mkd')}
                        </MediumText>
                    </Box>
                )}
                <Divider sx={{ my: 2 }} />

                {!!cart?.wallet && (
                    <Box display="flex" width="100%" alignItems="center" justifyContent="space-between">
                        {' '}
                        <MediumText>{t('wallet')}</MediumText>
                        <MediumText>
                            {cart?.wallet ? '-' : '0'}
                            {cart?.wallet} {t('mkd')}
                        </MediumText>
                    </Box>
                )}

                {!!cart?.takeoutDiscount && (
                    <Box display="flex" flexDirection="column">
                        {/* <Box display="flex" width="100%" alignItems="center" justifyContent="space-between">
                            {' '}
                            <MediumText>{t('subtotal')}</MediumText>
                            <MediumText>
                                {getFinalCartPrice() + cart.takeoutDiscount} {t('mkd')}
                            </MediumText>
                        </Box> */}
                        <Box display="flex" width="100%" alignItems="center" justifyContent="space-between">
                            {' '}
                            <MediumText>{t('takeoutDiscount')}</MediumText>
                            <MediumText>
                                -{cart.takeoutDiscount} {t('mkd')}
                            </MediumText>
                        </Box>
                    </Box>
                )}
                <Box display="flex" width="100%" alignItems="center" justifyContent="space-between">
                    {' '}
                    <MediumText semibold>{t('total_sum')}</MediumText>
                    {loadingPrice ? (
                        <CircularProgress size="25px" />
                    ) : (
                        <Box>
                            <MediumText semibold>
                                {!isNaN(getFinalCartPrice(paymentType === 'online'))
                                    ? getFinalCartPrice(paymentType === 'online')
                                    : 0}{' '}
                                {t('mkd')}
                            </MediumText>
                        </Box>
                    )}
                </Box>
                {cart?.groupedItems && Object.keys(cart?.groupedItems)?.length === 1 && <Coupon />}

                {/* {!cart?.coupon?.couponId &&
                    cart?.groupedItems &&
                    Object.values(cart?.groupedItems)?.[0]?.restaurantMinOrderAmount >
                        Object.values(cart?.groupedItems)?.[0]?.restaurantTotalItemsPrice && (
                        <Box display="flex" alignItems="center" justifyContent="space-between">
                            <Box display="flex" flexDirection="column">
                                {' '}
                                <MediumText>{t('roundup')}</MediumText>{' '}
                                <NormalText color="gray">
                                    *{t('min_items')}{' '}
                                    {cart?.groupedItems && Object.values(cart?.groupedItems)?.[0]?.restaurantMinOrderAmount}{' '}
                                    {t('mkd')}
                                </NormalText>
                            </Box>
                            <Box display="flex" alignItems="center">
                                <CartSwitch
                                    checked={roundUp}
                                    handleSwitch={bool => {
                                        setRoundUp(bool);
                                        addRoundup(bool, Object.values(cart?.groupedItems)?.[0]?.restaurantId);
                                    }}
                                />
                            </Box>
                        </Box>
                    )} */}

                {currentUser?.wallet?.amount > 0 && (
                    <Box pt={3} display="flex" alignItems="center" justifyContent="space-between">
                        <Box display="flex" flexDirection="column">
                            <Box display="flex" alignItems="center">
                                <img src={walletIcon} width="20px" />
                                <MediumText sx={{ ml: 1 }}> {t('useWallet')}: </MediumText>
                            </Box>
                            <NormalText color="gray">
                                {t('walletBalance')}: {(currentUser?.wallet?.amount - getUsedWalletMoney()).toFixed(2)} {t('mkd')}
                            </NormalText>{' '}
                        </Box>
                        <Box display="flex" alignItems="center">
                            <CartSwitch
                                checked={useWallet}
                                handleSwitch={bool => {
                                    addWallet(bool);
                                    setUseWallet(bool);
                                }}
                            />
                        </Box>
                    </Box>
                )}
            </Box>
            {loading ? (
                <Box py={4} display="flex" justifyContent="center">
                    {' '}
                    <CircularProgress />
                </Box>
            ) : (
                <Box mt={{ xs: 4, md: 4 }} display="flex" flexDirection="column">
                    <PrimaryButton disabled={disabled} onClick={() => onProceed?.()}>
                        {t('placeOrder')}
                    </PrimaryButton>
                </Box>
            )}
            <Render when={!!error}>
                <Box display="flex" alignItems="center" sx={{ color: 'red' }}>
                    <ErrorIcon /> <MediumText sx={{ ml: 1 }}>{error}</MediumText>
                </Box>
            </Render>
        </Box>
    );
};

export default CartFees;
